import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "../../../api-client/APIClient";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import {
  API_KEYS_PATH,
  API_KEYS_CREATE_PATH,
  API_KEYS_CREATE_TEST_PATH,
  DOCS_AUTHENTICATION_PATH,
  navigateToTestLinkedAccountsTablePage,
} from "../../../router/RouterUtils";
import {
  Button,
  ButtonVariant,
  Checkbox,
  Dialog,
  TextField,
  TextFieldVariant,
  Card,
  Text,
  Link,
  CopyTextField,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import { Plus, Trash } from "lucide-react";
import { SectionHeaderWrapper } from "../../shared/SectionHeaderWrapper";
import { RefreshCw } from "lucide-react";
import IconGrayTextCard from "./IconGrayTextCard";
import { formatDate } from "../../../models/Helpers";
import PortalPageHeaderWrapper from "../../portal/PortalPageHeaderWrapper";
import { useHistory, useLocation } from "react-router-dom";
import useAppContext from "../../context/useAppContext";
import { UserType } from "../../../models/Entities";

type APIKeyInfo = {
  id: string;
  key_type: string;
  created_at: string;
};

type APIKey = APIKeyInfo & {
  key: string;
};

type RemoteAPIKeyInfo = APIKeyInfo & {
  name: string;
  is_test: boolean;
};

type APIKeyData = [RemoteAPIKeyInfo];

const ConfigurationAPIKeysPage = () => {
  const { user } = useAppContext();

  const [remoteAPIKeyData, setRemoteAPIKeyData] = useState<APIKeyData | undefined>();
  const [keyBeingRegenerated, setKeyBeingRegenerated] = useState<"production" | undefined>();
  const [regenerateConfirmInputText, setRegenerateConfirmInputText] = useState("");
  const [isLoadingRegenerateKey, setIsLoadingRegenerateKey] = useState(false);
  const [isLoadingNewRemoteKey, setIsLoadingNewRemoteKey] = useState(false);
  const [isLoadingDeleteKey, setIsLoadingDeleteKey] = useState(false);
  const [generatedAPIKey, setGeneratedAPIKey] = useState<APIKey | undefined>();
  const [keyBeingDeleted, setKeyBeingDeleted] = useState<APIKeyInfo | undefined>();
  const [isShowingConfirmDeleteModal, setIsShowingConfirmDeleteModal] = useState(false);
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);
  const [newRemoteKeyName, setNewRemoteKeyName] = useState("");
  const [newRemoteKeyIsProd, setNewRemoteKeyIsProd] = useState(false);
  const history = useHistory();
  const createDialogSession = useRef(0);
  const location = useLocation();
  const { pathname } = location;
  const newRemoteKeyIsTest = pathname === API_KEYS_CREATE_TEST_PATH;
  const isShowingAddRemoteKeyModal = pathname === API_KEYS_CREATE_PATH || newRemoteKeyIsTest;

  const userIsNotAdminWithBilling = user.type !== UserType.admin_with_billing;

  useEffect(() => {
    if (isShowingAddRemoteKeyModal) {
      setNewRemoteKeyName("");
      setGeneratedAPIKey(undefined);
      setNewRemoteKeyIsProd(false);
      setIsLoadingNewRemoteKey(false);
    }
    return () => {
      createDialogSession.current += 1;
    };
  }, [isShowingAddRemoteKeyModal, location]);

  useEffect(() => {
    fetchWithAuth({
      path: "/integrations/remote-keys",
      method: "GET",
      onResponse: (data) => {
        setRemoteAPIKeyData(data);
      },
    });
  }, []);

  const regenerateAPIKey = useCallback(() => {
    setIsLoadingRegenerateKey(true);
    setNewRemoteKeyIsProd(true);
    fetchWithAuth({
      path: `/integrations/keys/regenerate-production-key`,
      method: "POST",
      onResponse: (generatedProdAPIKey: APIKey) => {
        setGeneratedAPIKey(generatedProdAPIKey);
        setIsLoadingRegenerateKey(false);
        setNewRemoteKeyName("");
      },
      onError: () => {
        showErrorToast(
          `Failed to regenerate your organization's ${keyBeingRegenerated} key. Please try again.`,
        );
        setIsLoadingRegenerateKey(false);
        setKeyBeingRegenerated(undefined);
        setNewRemoteKeyName("");
      },
    });
  }, [keyBeingRegenerated, remoteAPIKeyData]);

  const createRemoteKey = useCallback(() => {
    if (!newRemoteKeyName.trim() || isLoadingNewRemoteKey || userIsNotAdminWithBilling) {
      return;
    }
    const requestLocation = history.location;
    const requestSession = createDialogSession.current;
    setIsLoadingNewRemoteKey(true);
    fetchWithAuth({
      path: "/integrations/remote-key",
      method: "POST",
      body: { name: newRemoteKeyName.trim(), is_test: newRemoteKeyIsTest },
      onResponse: (generatedRemoteAPIKey: APIKey) => {
        if (
          history.location === requestLocation &&
          createDialogSession.current === requestSession
        ) {
          setGeneratedAPIKey(generatedRemoteAPIKey);
          setIsLoadingNewRemoteKey(false);
        }
        fetchWithAuth({
          path: "/integrations/remote-keys",
          method: "GET",
          onResponse: (data) => {
            setRemoteAPIKeyData(data);
          },
        });
      },
      onError: (response: Response | undefined) => {
        if (
          history.location !== requestLocation ||
          createDialogSession.current !== requestSession
        ) {
          return;
        }
        // We catch the specific issue where we get a 409 Conflict error
        // and display a specific error toast for it.
        if (response && response.status === 409) {
          showErrorToast(
            `A key named ${newRemoteKeyName} already exists. Please select a different key name.`,
          );
        } else {
          showErrorToast(
            `Failed to generate your organization's ${newRemoteKeyName} key. Please try again.`,
          );
        }
        setIsLoadingNewRemoteKey(false);
      },
    });
  }, [
    newRemoteKeyName,
    newRemoteKeyIsTest,
    isLoadingNewRemoteKey,
    userIsNotAdminWithBilling,
    history,
  ]);

  const emptyState = (isTest: boolean) => {
    return (
      <div className="px-5 py-4">
        <Text>
          You have no {isTest ? "test" : "remote production"} access keys. Add an access key by
          clicking the button above.
        </Text>
      </div>
    );
  };

  const getKeyRows = (isTest: boolean) => {
    const keyData = remoteAPIKeyData?.filter((remote_key) => remote_key.is_test == isTest) ?? [];

    if (!remoteAPIKeyData) {
      return Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-row items-center justify-between p-4 border-b border-gray-10 last:border-b-0"
        >
          <SkeletonLoader height={20} borderRadius={8} fullWidth />
        </div>
      ));
    } else if (keyData.length > 0) {
      return keyData.map((remote_key, index) => {
        return (
          <div className="flex flex-row items-center justify-between p-4 border-b border-gray-10 last:border-b-0">
            {/* API key name is a user-chosen label and is thus masked at the cell */}
            <Text variant="h6" className="ph-no-capture">
              {remote_key.name}
            </Text>
            <div className="flex flex-row items-center gap-x-8">
              <div>
                <Text className="text-gray-40">Created</Text>{" "}
                <Text>{formatDate(remote_key.created_at, "MMM DD, YYYY", false)}</Text>
              </div>
              <Button
                variant={ButtonVariant.IconOnly}
                size="sm"
                onClick={() => {
                  setKeyBeingDeleted(remote_key);
                  setDeleteConfirmChecked(false);
                  setIsShowingConfirmDeleteModal(true);
                }}
                disabled={userIsNotAdminWithBilling}
              >
                <Trash size={16} />
              </Button>
            </div>
          </div>
        );
      });
    } else {
      return emptyState(isTest);
    }
  };

  const getKeyTable = (isTest: boolean) => {
    return (
      <div className="flex flex-col border border-gray-10 rounded-md">{getKeyRows(isTest)}</div>
    );
  };

  const deleteRemoteKey = () => {
    if (!keyBeingDeleted) {
      return;
    }
    setIsLoadingDeleteKey(true);
    fetchWithAuth({
      path: `/integrations/remote-key/${keyBeingDeleted.id}`,
      method: "DELETE",
      onResponse: () => {
        setKeyBeingDeleted(undefined);
        setIsShowingConfirmDeleteModal(false);
        fetchWithAuth({
          path: "/integrations/remote-keys",
          method: "GET",
          onResponse: (data) => {
            setRemoteAPIKeyData(data);
            showSuccessToast("Successfully deleted key!");
            setIsLoadingDeleteKey(false);
          },
        });
      },
    });
  };

  const getGeneratedKey = (
    // Generated access key is a secret and is thus masked at the dialog body
    // from both PostHog autocapture and the Frigade Assistant DOM snapshot.
    <div className="ph-no-capture" data-fr-ignore>
      <p>Copy your access key below and record it somewhere safe.</p>
      <p className="font-bold">
        You will <span className="red">not</span> be able to recover this key after closing this
        window.
      </p>
      <CopyTextField
        className="my-6"
        text={generatedAPIKey?.key ?? "Loading..."}
        secret={!!generatedAPIKey}
      />
    </div>
  );

  const keyType = newRemoteKeyIsTest ? "test" : newRemoteKeyIsProd ? "production" : "remote";
  const modalTitle = keyBeingRegenerated
    ? `Regenerate ${keyBeingRegenerated} access token`
    : `${isShowingAddRemoteKeyModal ? "Add" : "New"} ${keyType} access key`;

  const onCloseAddRemoteKeyModal = () => {
    history.replace(API_KEYS_PATH);
  };

  return (
    <PortalPageHeaderWrapper
      title="API keys"
      subtitle="Access your production and test access keys"
    >
      <>
        <Dialog
          open={isShowingAddRemoteKeyModal}
          onClose={onCloseAddRemoteKeyModal}
          onPrimaryButtonClick={createRemoteKey}
          primaryButtonDisabled={
            !newRemoteKeyName.trim() || isLoadingNewRemoteKey || userIsNotAdminWithBilling
          }
          primaryButtonLoading={isLoadingNewRemoteKey}
          primaryButtonVariant={ButtonVariant.PrimaryGray}
          primaryButtonText="Create"
          onSecondaryButtonClick={onCloseAddRemoteKeyModal}
          secondaryButtonText="Cancel"
          secondaryButtonVariant={ButtonVariant.TextBlack}
          compactFooterButtons
          footerButtonsHidden={!!generatedAPIKey}
          title={modalTitle}
          variant="md"
        >
          {generatedAPIKey ? (
            getGeneratedKey
          ) : (
            <div className="flex flex-col gap-4">
              <Text>
                This key only provides access to {newRemoteKeyIsTest ? "test" : "production"} Linked
                Accounts in your Merge instance.
              </Text>
              {userIsNotAdminWithBilling && (
                <Text>Only admins with billing access can create access keys.</Text>
              )}
              <TextField
                labelTitle="Key name"
                placeholder="Enter a key name"
                value={newRemoteKeyName}
                onChange={(e) => setNewRemoteKeyName(e.target.value)}
              />
            </div>
          )}
        </Dialog>

        <Dialog
          open={!!keyBeingRegenerated}
          onClose={() => {
            setKeyBeingRegenerated(undefined);
            setGeneratedAPIKey(undefined);
          }}
          onPrimaryButtonClick={regenerateAPIKey}
          primaryButtonDisabled={regenerateConfirmInputText !== keyBeingRegenerated}
          primaryButtonLoading={isLoadingRegenerateKey}
          primaryButtonText="Delete and regenerate token"
          onSecondaryButtonClick={() => {
            setKeyBeingRegenerated(undefined);
            setGeneratedAPIKey(undefined);
          }}
          secondaryButtonText="Cancel"
          footerButtonsHidden={!!generatedAPIKey}
          title={modalTitle}
          variant="md"
        >
          {generatedAPIKey ? (
            <>{getGeneratedKey}</>
          ) : (
            <>
              <p>
                Regenerating will permanently delete any existing {keyBeingRegenerated} tokens
                currently in use.
              </p>
              <p className="font-bold">
                Type "<span className="red">{keyBeingRegenerated}</span>" below to confirm.
              </p>

              <TextField
                placeholder={keyBeingRegenerated}
                className="my-6"
                onChange={(e) => setRegenerateConfirmInputText(e.currentTarget.value)}
                variant={TextFieldVariant.Bordered}
              />
            </>
          )}
        </Dialog>

        <Dialog
          open={isShowingConfirmDeleteModal}
          onClose={() => setIsShowingConfirmDeleteModal(false)}
          onPrimaryButtonClick={() => {
            deleteRemoteKey();
          }}
          onSecondaryButtonClick={() => {
            setIsShowingConfirmDeleteModal(false);
          }}
          primaryButtonText="Delete key"
          primaryButtonVariant={ButtonVariant.DangerFilled}
          primaryButtonLoading={isLoadingDeleteKey}
          primaryButtonDisabled={!deleteConfirmChecked}
          secondaryButtonText="Cancel"
          secondaryButtonVariant={ButtonVariant.TextBlack}
          title="Delete access key"
          variant="md"
        >
          <div className="flex flex-col gap-4">
            <Text>This action cannot be undone.</Text>
            <Text>Do you wish to continue?</Text>
            <Checkbox
              checked={deleteConfirmChecked}
              onChange={setDeleteConfirmChecked}
              label="I understand that this is an irreversible action"
            />
          </div>
        </Dialog>
        <SectionHeaderWrapper
          title="Organization secrets"
          subtitle={
            <>
              <Text as="p" className="mb-3">
                Use your access keys below to authenticate requests to Merge's APIs.
                <br />
              </Text>{" "}
              <Text as="p" className="mb-3">
                Never put plaintext secrets in your codebase. We highly suggest using a secrets
                manager to load in secrets at runtime to your application.
              </Text>
            </>
          }
          docsUrl={DOCS_AUTHENTICATION_PATH}
        >
          <div className="flex flex-col gap-y-8">
            <Card className="p-6 flex flex-row flex-wrap align-items-center justify-content-between">
              <div className="flex flex-col">
                <div className="flex flex-row items-center">
                  <Text variant="h5">Production access key</Text>
                  <IconGrayTextCard textLength={20} />
                </div>
                <div className="mt-3">
                  <Text>
                    Use this key when interacting with the Merge API from your production
                    environment.
                  </Text>
                </div>
              </div>
              <div className="flex mt-3 md:mt-0">
                <Button
                  variant={ButtonVariant.TertiaryWhite}
                  size="sm"
                  leftIcon={<RefreshCw size={12} />}
                  onClick={() => {
                    setGeneratedAPIKey(undefined);
                    setKeyBeingRegenerated("production");
                  }}
                  disabled={userIsNotAdminWithBilling}
                >
                  Regenerate token
                </Button>
              </div>
            </Card>
            <Card className="p-6 flex-col">
              <div className="flex flex-row flex-wrap align-items-center justify-content-between items-start">
                <div className="flex flex-col">
                  <Text variant="h5" className="mb-3">
                    Remote production access keys
                  </Text>
                  <Text className="mb-3">
                    These keys give full access to production data, and are typically used for
                    on-premise deploys.
                  </Text>
                  <Text className="mb-3">
                    They are different from your production access key since they cannot be used to
                    generate additional remote production access keys.
                  </Text>
                  <Text>Most organizations will not need these.</Text>
                </div>
                <div className="flex sm:mt-3">
                  <Button
                    variant={ButtonVariant.TertiaryWhite}
                    size="sm"
                    leftIcon={<Plus size={12} />}
                    onClick={() => history.push(API_KEYS_CREATE_PATH)}
                    disabled={userIsNotAdminWithBilling}
                  >
                    Remote key
                  </Button>
                </div>
              </div>
              <div className="mt-5">{getKeyTable(false)}</div>
            </Card>
            <Card className="p-6 w-100 flex-col">
              <div className="flex flex-row flex-wrap align-items-center justify-content-between items-start">
                <div className="flex flex-col">
                  <Text variant="h5" className="mb-3">
                    Test access keys
                  </Text>
                  <Text>
                    Using these keys, you can programmatically create{" "}
                    <Link onClick={() => navigateToTestLinkedAccountsTablePage(history)} as="span">
                      test Linked Accounts
                    </Link>{" "}
                    and communicate with the Merge API for data related to test Linked Accounts.
                  </Text>
                </div>
                <div className="flex mt-3 md:mt-0">
                  <Button
                    variant={ButtonVariant.TertiaryWhite}
                    size="sm"
                    leftIcon={<Plus size={12} />}
                    onClick={() => history.push(API_KEYS_CREATE_TEST_PATH)}
                    disabled={userIsNotAdminWithBilling}
                  >
                    Test key
                  </Button>
                </div>
              </div>
              <div className="mt-5">{getKeyTable(true)}</div>
            </Card>
          </div>
        </SectionHeaderWrapper>
      </>
    </PortalPageHeaderWrapper>
  );
};

export default ConfigurationAPIKeysPage;
